import React, {
  useEffect,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";

import axios from "axios";
import ReactLoading from "react-loading";
import { useTranslation } from "react-i18next";
import SelectSingle from "react-select";

import useDirtyState from "../../../../hooks/useDirtyState";
import customStyles from "../../../../assets/select/customStyles";

const SmartSignSettingsBackground = forwardRef(function (props, ref) {
  const { t } = useTranslation();
  const link = localStorage.getItem("link");
  const token = localStorage.getItem("JWT");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function getData() {
    axios.defaults.headers.common["Authorization"] =
      "Bearer " + localStorage.getItem("JWT");

    setLoading(true);
    axios
      .get(link + "/api/SmartSignageBackgroundImage")
      .then((response) => {
        setImages(response.data);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    getData();
  }, []);

  const data = props.data;

  const [renderImages, setRenderImages] = useState([]);

  const backgroundOptions = useMemo(
    () => [
      {
        value: null,
        label: (
          <div className="flex-iac settings-item">
            {t("allgemein.standard")}
          </div>
        ),
      },
      ...renderImages.map((img) => ({
        value: img.id,
        label: <div className="flex-iac settings-item">{img.name}</div>,
      })),
    ],
    [renderImages, t],
  );

  const [selectedPreview, setSelectedPreview] = useState(null);

  const state = useDirtyState(props.initialSettings || null);

  async function loadProtectedImage(url) {
    try {
      const response = await axios.get(`${link}${url}`, {
        responseType: "blob",

        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error("Image loading failed", error);

      return null;
    }
  }

  useEffect(
    function () {
      async function prepareImages() {
        try {
          const prepared = await Promise.all(
            images.map(async function (img) {
              const thumbnailBlobUrl = await loadProtectedImage(
                img.thumbnailUrl,
              );

              return {
                ...img,
                thumbnailBlobUrl,
              };
            }),
          );

          setRenderImages(prepared);
        } catch (error) {
          console.error(error);
        }
      }

      if (images.length > 0) {
        prepareImages();
      }
    },
    [images],
  );

  useEffect(
    function () {
      async function loadPreview() {
        if (!state.value) {
          setSelectedPreview(null);
          return;
        }

        const selectedImage = renderImages.find(function (img) {
          return String(img.id) === String(state.value);
        });

        if (!selectedImage) {
          setSelectedPreview(null);
          return;
        }

        const fullBlobUrl = await loadProtectedImage(selectedImage.url);

        setSelectedPreview(fullBlobUrl);
      }

      loadPreview();
    },
    [state.value, renderImages],
  );

  useEffect(() => {
    return function () {
      renderImages.forEach(function (img) {
        if (img.thumbnailBlobUrl) {
          URL.revokeObjectURL(img.thumbnailBlobUrl);
        }
      });

      if (selectedPreview) {
        URL.revokeObjectURL(selectedPreview);
      }
    };
  }, [renderImages, selectedPreview]);

  function update(value) {
    state.update(value);

    if (props.onUpdate) {
      props.onUpdate(value);
    }
  }

  useImperativeHandle(ref, function () {
    const save = async () => {
      if (!state.hasChanges() || saving) return;
      setSaving(true);
      try {
        const imageIdQuery = state.value
          ? `&imageId=${encodeURIComponent(state.value)}`
          : "&imageId=";

        await axios.put(
          `${link}/api/SmartSignage/BackgroundImage?elementId=${data.id}${imageIdQuery}`,
        );
        state.resetDirty();
      } finally {
        setSaving(false);
      }
    };

    return {
      getSettings: state.getValue,
      hasChanges: state.hasChanges,
      resetChanges: state.resetDirty,
      isDirty: state.hasChanges,
      save,
      reset: () => update(props.initialSettings || null),
    };
  });

  return (
    <div className="settings-groups">
      <style>
        {`
          .background-preview-wrapper {
            width: 100%;
            max-width: 500px;
            height: 160px;
            border-radius: 8px;
            overflow: hidden;
            border: 1px solid #d9d9d9;
            background: #f5f5f5;
            margin-top: 10px;
          }

          .background-preview-image {
            width: 100%;
            height: 100%;
            object-fit: contain;
            display: block;
          }

          .background-select {
            width: 100%;
            height: 36px;
          }
        `}
      </style>

      <div className="settings-name">{t("smartSign.background")}</div>

      <div className="background-select">
        <SelectSingle
          className="select"
          styles={customStyles}
          isSearchable={false}
          isDisabled={loading || saving}
          value={
            backgroundOptions.find(
              (option) => String(option.value) === String(state.value),
            ) || backgroundOptions[0]
          }
          onChange={(option) => update(option?.value ?? null)}
          options={backgroundOptions}
          theme={(theme) => ({
            ...theme,
            borderRadius: 3,
            colors: { ...theme.colors, primary: "#20a0ff" },
          })}
        />
      </div>

      {loading && (
        <ReactLoading type="bars" color="#2a3042" height={45} width={45} />
      )}

      {selectedPreview && (
        <div style={{ marginTop: "15px" }}>
          <div className="settings-name">{t("allgemein.vorschau")}</div>

          <div className="background-preview-wrapper">
            <img
              src={selectedPreview}
              alt="preview"
              className="background-preview-image"
            />
          </div>
        </div>
      )}
    </div>
  );
});

export default SmartSignSettingsBackground;
